import { Button } from 'base/ui/button';
import { Checkbox } from 'base/ui/checkbox';
import { Dialog } from 'base/ui/dialog';
import { RedErrorText, useSubmit } from 'base/ui/form';
import { Rows } from 'base/ui/layout';
import { menuItem } from 'base/ui/menu_model';
import { TextRegular, TextRegularLight } from 'base/ui/typography';
import { BlocksService, NotificationSubscription } from 'blocks/blocks_service';
import { EnumHashParameterStore } from 'blocks/hash_parameter';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { ProjectSession } from '../session/session';

const notificationSettingsUrlParam = new EnumHashParameterStore(
  'notificationSettings',
  ['open', 'closed'],
  'closed',
);
export const openNotificationSettings = () =>
  notificationSettingsUrlParam.set('open');
const close = () => notificationSettingsUrlParam.set(undefined);

export const notificationsMenuItem = menuItem({
  label: 'Notifications',
  onClick: openNotificationSettings,
});

export const NotificationSettings = observer(function _NotificationSettings({
  session,
  blocksService,
}: {
  session: ProjectSession;
  blocksService: BlocksService;
}) {
  if (notificationSettingsUrlParam.get() !== 'open') {
    return null;
  }
  if (session.InlineAuthDialog) {
    return (
      <session.InlineAuthDialog
        onClose={close}
        signupTitle="Create an account to manage notification settings"
        isCommenter={true}
      />
    );
  }
  return (
    <SettingsDialog
      projectToken={session.projectToken}
      blocksService={blocksService}
    />
  );
});

function SettingsDialog({
  projectToken,
  blocksService,
}: {
  projectToken: string;
  blocksService: BlocksService;
}) {
  const [saved, setSaved] = React.useState<
    NotificationSubscription | undefined
  >();
  const [initialError, setInitialError] = React.useState<string | undefined>();

  React.useEffect(() => {
    blocksService
      .projectGetNotificationSubscription(projectToken)
      .then(setSaved)
      .catch(setInitialError);
  }, [blocksService, projectToken]);

  const [updating, updateError, updateTo] = useSubmit(
    (preference: NotificationSubscription['preference']) =>
      blocksService
        .projectUpdateNotificationSubscription({
          projectToken,
          preference,
        })
        .then(setSaved),
  );

  const loading = updating || (saved == null && initialError == null);
  const radioProps = {
    disabled: loading,
    size: 'large',
    labelColor: 'black',
    type: 'radio',
  } as const;
  return (
    <Dialog
      title="Notifications"
      onClose={close}
      buttons={
        <Button
          variant="primary"
          size="large"
          label="Save"
          onClick={close}
          loading={loading}
        />
      }
    >
      <Rows spacing={8}>
        <TextRegularLight>
          Email <TextRegular tag="span">{saved?.email || 'me'}</TextRegular>{' '}
          about:
        </TextRegularLight>
        <Checkbox
          label="All comments"
          value={saved?.preference === 'all'}
          onChange={(checked) => checked && updateTo('all')}
          {...radioProps}
        />
        <Checkbox
          label="Only replies"
          value={saved?.preference === 'replies-only'}
          onChange={(checked) => checked && updateTo('replies-only')}
          {...radioProps}
        />
        <Checkbox
          label="None"
          value={saved?.preference === 'none'}
          onChange={(checked) => checked && updateTo('none')}
          {...radioProps}
        />
        <div />
        <RedErrorText error={updateError || initialError} />
        <TextRegularLight>
          Preferences apply to this project only.
        </TextRegularLight>
      </Rows>
    </Dialog>
  );
}
